/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   AccessGate.tsx
 *     Description     :   Spec 285 — render `children` only when the current principal may
 *                         access `area` (role ∧ license, SuperAdmin exempt), decided by the
 *                         shared canAccessArea the backend guard also uses. For gated controls
 *                         embedded in otherwise-open pages: dashboard backup tiles, Protection
 *                         tabs/panels, the Workspace Insights client assignment. A whole gated
 *                         PAGE uses ProtectedRoute's requiredArea instead.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React from 'react';
import type { NavAreaId } from '@shared/access/navAccessPolicy';
import { useHasAccess } from '../../hooks/useAccess';

export interface AccessGateProps {
  area: NavAreaId;
  children: React.ReactNode;
  /** Rendered instead of `children` when access is refused. Default: nothing. */
  fallback?: React.ReactNode;
}

export const AccessGate: React.FC<AccessGateProps> = ({ area, children, fallback = null }) => {
  const allowed = useHasAccess(area);
  return <>{allowed ? children : fallback}</>;
};

export default AccessGate;
